import { Button, Input } from '@fsd/ui';
import {
  AddSquare,
  CheckCircle,
  DangerCircle,
  GalleryAdd,
  InfoCircle,
  Link,
  Star,
  TrashBinMinimalistic,
  Upload,
} from '@solar-icons/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useRef, useState } from 'react';
import { uploadImageToR2, validateImageFile } from '../../services/imageUpload';

interface ImageListProps {
  images: string[];
  onChange: (images: string[]) => void;
  error?: string;
}

type UploadMode = 'url' | 'file';

function isValidImageUrl(url: string): boolean {
  if (!url || url.trim() === '') return false;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

export function ImageList({ images, onChange, error }: ImageListProps) {
  const [newImageUrl, setNewImageUrl] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [uploadMode, setUploadMode] = useState<UploadMode>('url');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loadingStates, setLoadingStates] = useState<
    Record<string, 'loading' | 'success' | 'error'>
  >({});

  const handleAddImage = () => {
    if (!isValidImageUrl(newImageUrl)) return;
    onChange([...images, newImageUrl]);
    setNewImageUrl('');
    setIsAdding(false);
    setUploadError(null);
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    if (!file) return;

    // Validate file
    const validation = validateImageFile(file);
    if (!validation.valid) {
      setUploadError(validation.error || 'Arquivo inválido');
      return;
    }

    setUploadError(null);
    setIsUploading(true);
    setUploadProgress(0);

    try {
      const result = await uploadImageToR2(file, {
        quality: 0.8,
        onProgress: setUploadProgress,
      });

      onChange([...images, result.url]);
      setIsAdding(false);
      setUploadProgress(0);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao fazer upload';
      setUploadError(errorMessage);
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleModeChange = (mode: UploadMode) => {
    setUploadMode(mode);
    setUploadError(null);
    setNewImageUrl('');
  };

  const removeImage = (index: number) => {
    onChange(images.filter((_, i) => i !== index));
  };

  const handleSetPrincipal = (index: number) => {
    if (index === 0) return;
    const newImages = [...images];
    const [selected] = newImages.splice(index, 1);
    if (!selected) return;
    newImages.unshift(selected);
    onChange(newImages);
  };

  const handleImageLoad = (url: string) => {
    setLoadingStates((prev) => ({ ...prev, [url]: 'success' }));
  };

  const handleImageError = (url: string) => {
    setLoadingStates((prev) => ({ ...prev, [url]: 'error' }));
  };

  // Helper to get status for a specific URL
  const getStatus = (url: string) => loadingStates[url] || 'loading';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <span className="text-sm font-medium text-foreground flex items-center gap-1">
            Galeria de Imagens <span className="text-destructive">*</span>
          </span>
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <InfoCircle size={12} />A primeira imagem será usada como capa principal.
          </p>
        </div>
        <span className="text-xs font-medium text-muted-foreground bg-muted px-2 py-1 rounded-full">
          {images.length} {images.length === 1 ? 'imagem' : 'imagens'}
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <AnimatePresence mode="popLayout">
          {images
            .filter((url) => !!url)
            .map((url, index) => (
              <motion.div
                layout
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                key={url}
                className={`group relative aspect-4/3 rounded-xl border border-border bg-card overflow-hidden shadow-sm transition-all hover:shadow-md ${
                  index === 0 ? 'ring-2 ring-primary ring-offset-1 ring-offset-background' : ''
                }`}
              >
                {/* Image & Status */}
                <div className="absolute inset-0 bg-muted flex items-center justify-center">
                  {getStatus(url) === 'loading' && (
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                  )}
                  {getStatus(url) === 'error' && (
                    <div className="flex flex-col items-center gap-1 text-destructive p-2 text-center">
                      <DangerCircle size={24} />
                      <span className="text-[10px]">Erro ao carregar</span>
                    </div>
                  )}
                </div>
                <img
                  src={url}
                  alt={`Product ${index}`}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
                    getStatus(url) === 'success' ? 'opacity-100' : 'opacity-0'
                  }`}
                  onLoad={() => handleImageLoad(url)}
                  onError={() => handleImageError(url)}
                />

                {/* Overlays */}
                <div className="absolute inset-0 bg-linear-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />

                {/* Badges */}
                {index === 0 && (
                  <div className="absolute top-2 left-2 bg-primary/90 backdrop-blur-sm text-primary-foreground text-[10px] uppercase font-bold px-2 py-1 rounded-md shadow-sm flex items-center gap-1">
                    <Star size={10} /> Principal
                  </div>
                )}

                {/* Actions */}
                <div className="absolute bottom-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-all transform translate-y-2 group-hover:translate-y-0">
                  {index !== 0 && (
                    <button
                      type="button"
                      onClick={() => handleSetPrincipal(index)}
                      className="p-1.5 bg-white/10 hover:bg-white/20 backdrop-blur-md text-white rounded-md transition-colors"
                      title="Definir como Principal"
                    >
                      <Star size={16} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="p-1.5 bg-red-500/80 hover:bg-red-500 backdrop-blur-md text-white rounded-md transition-colors"
                    title="Remover"
                  >
                    <TrashBinMinimalistic size={16} />
                  </button>
                </div>
              </motion.div>
            ))}
        </AnimatePresence>

        {/* Add New Card */}
        {isAdding ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="aspect-4/3 rounded-xl border-2 border-dashed border-primary/50 bg-primary/5 p-3 flex flex-col gap-2 justify-center items-center relative"
          >
            <div className="w-full space-y-2">
              {/* Mode Switcher */}
              <div className="flex gap-1 bg-background/50 rounded-md p-0.5">
                <button
                  type="button"
                  onClick={() => handleModeChange('url')}
                  className={`flex-1 px-2 py-1 rounded text-[10px] font-medium transition-colors ${
                    uploadMode === 'url'
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Link size={12} className="inline mr-1" />
                  URL
                </button>
                <button
                  type="button"
                  onClick={() => handleModeChange('file')}
                  className={`flex-1 px-2 py-1 rounded text-[10px] font-medium transition-colors ${
                    uploadMode === 'file'
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Upload size={12} className="inline mr-1" />
                  Upload
                </button>
              </div>

              {/* URL Mode */}
              {uploadMode === 'url' && (
                <>
                  <div className="relative">
                    <Input
                      autoFocus
                      value={newImageUrl}
                      onChange={(e) => setNewImageUrl(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddImage();
                        }
                        if (e.key === 'Escape') {
                          setIsAdding(false);
                          setNewImageUrl('');
                        }
                      }}
                      placeholder="https://..."
                      className="h-8 text-xs pr-8 bg-background/50"
                    />
                    {isValidImageUrl(newImageUrl) && (
                      <CheckCircle size={12} className="absolute right-2 top-2.5 text-green-500" />
                    )}
                  </div>
                  <div className="flex gap-1.5">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setIsAdding(false);
                        setUploadError(null);
                      }}
                      className="flex-1 h-7 text-xs"
                    >
                      Cancelar
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleAddImage}
                      disabled={!isValidImageUrl(newImageUrl)}
                      className="flex-1 h-7 text-xs"
                    >
                      Adicionar
                    </Button>
                  </div>
                </>
              )}

              {/* File Upload Mode */}
              {uploadMode === 'file' && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                    onChange={handleFileSelect}
                    className="hidden"
                  />

                  {isUploading ? (
                    <div className="space-y-2">
                      <div className="flex items-center justify-center gap-2 text-primary">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
                        <span className="text-xs font-medium">Convertendo para WebP...</span>
                      </div>
                      <div className="w-full bg-muted/50 rounded-full h-2 overflow-hidden">
                        <div
                          className="bg-primary h-full transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                      <p className="text-[10px] text-center text-muted-foreground">
                        {uploadProgress}%
                      </p>
                    </div>
                  ) : (
                    <>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full h-8 text-xs gap-1.5"
                      >
                        <GalleryAdd size={14} />
                        Escolher Arquivo
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setIsAdding(false);
                          setUploadError(null);
                        }}
                        className="w-full h-6 text-xs"
                      >
                        Cancelar
                      </Button>
                      <p className="text-[9px] text-center text-muted-foreground">
                        JPG, PNG, WebP ou GIF (máx 10MB)
                      </p>
                    </>
                  )}
                </>
              )}

              {/* Upload Error */}
              {uploadError && (
                <div className="flex items-center gap-1 text-destructive text-[10px] bg-destructive/5 p-1.5 rounded border border-destructive/10">
                  <DangerCircle size={10} />
                  <span>{uploadError}</span>
                </div>
              )}
            </div>
          </motion.div>
        ) : (
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setIsAdding(true)}
            type="button"
            className="aspect-4/3 rounded-xl border-2 border-dashed border-border bg-muted/30 hover:bg-muted/50 hover:border-primary/50 transition-all flex flex-col items-center justify-center gap-2 group cursor-pointer text-muted-foreground hover:text-primary"
          >
            <div className="p-3 rounded-full bg-muted group-hover:bg-background transition-colors shadow-sm">
              <AddSquare size={24} />
            </div>
            <span className="text-xs font-medium">Adicionar Imagem</span>
          </motion.button>
        )}
      </div>

      {error ? (
        <div className="flex items-center gap-2 text-destructive text-sm bg-destructive/5 p-3 rounded-md border border-destructive/10">
          <DangerCircle size={16} />
          {error}
        </div>
      ) : (
        <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/30 p-3 rounded-md">
          <InfoCircle size={14} className="mt-0.5 shrink-0" />
          <div className="space-y-1">
            <p>
              <strong>Upload:</strong> Faça upload de imagens que serão automaticamente convertidas
              para WebP e enviadas para o Cloudflare R2 Storage.
            </p>
            <p>
              <strong>URL:</strong> Cole URLs de imagens já hospedadas (Unsplash, GitHub, etc).
            </p>
            <p className="text-[11px]">
              Recomendamos proporção 4:3 ou 16:9. Tamanho máximo para upload: 10MB.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
